import React, { useState } from 'react';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { useI18n } from '../../context/I18nContext';
import Button from '../ui/Button';
import { Download, Check } from 'lucide-react';

interface InstallPWAButtonProps {
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline';
  size?: 'sm' | 'md' | 'lg';
}

const InstallPWAButton: React.FC<InstallPWAButtonProps> = ({ 
  className = '',
  variant = 'primary',
  size = 'md'
}) => {
  const { isInstallable, isInstalled, promptInstall } = usePWAInstall();
  const { t } = useI18n();
  const [isInstalling, setIsInstalling] = useState(false);

  // If already installed, show success message
  if (isInstalled) {
    return (
      <div className={`flex items-center gap-2 text-sm text-green-600 ${className}`}>
        <Check className="w-4 h-4" />
        <span>{t('pwa.installed')}</span>
      </div>
    );
  }

  const handleInstall = async () => {
    if (!isInstallable) {
      // Installation not available via API, user needs manual steps
      return;
    }

    setIsInstalling(true);
    try {
      await promptInstall();
    } catch (error) {
      console.error('Installation error:', error);
    } finally {
      setIsInstalling(false);
    }
  };

  return (
    <Button
      onClick={handleInstall}
      variant={variant}
      size={size}
      loading={isInstalling}
      icon={<Download className="w-4 h-4" />}
      className={className}
      disabled={isInstalling || !isInstallable}
    >
      {t('pwa.install')}
    </Button>
  );
};

export default InstallPWAButton;

